import planTemplate from "../templates/pages/plan.html?raw";
import NavBar from "../sections/navbar.ts";
import { getStringFromLocalStorage, saveJsonToLocalStorage } from "../services/localStorage.ts";
import { Page } from "./base.ts";
import { getChildElementSelector, getElementById, setContent, setSmallLoading } from "../services/dom.ts";
import { getApi } from "../services/api.ts";
import { IUser } from "../types/user.ts";
import MealPlanItem from "../sections/mealplanitem.ts";
import Footer from "../sections/footer.ts";
import { Meals } from "../types/plan.ts";
import { DateAppends, Months, StandardWeekdays } from "../types/date.ts";

const apiUrl = import.meta.env.VITE_API_URL as string;
const apiToken = getStringFromLocalStorage("idToken");

export default class PlanPage extends Page {
  private dateList: Date[] = [];
  private householdUsers: IUser[] = [];
  private startDate: Date = new Date();
  private endDate: Date = new Date();
  private currentMonth: Date = new Date();
  private hoverDate: Date | null = null;
  private isSelectingRange: boolean = false;
  constructor() {
    super(planTemplate);
    this.loadPage();
  }

  private async loadPage(): Promise<void> {
    // Create navbar and footer immediately
    new NavBar();
    new Footer();

    // Configure any buttons to call functions
    this.setupListeners();
    // Handle any custom attributes (data-toggle-section, data-model)
    super.handleCustomAttributes();

    // Initialize stores and load data
    await super.init();
    await this.loadData();

    // Open the content
    setContent("plan");
  }

  private setupListeners(): void {
    // Initialize calendar with default values
    this.initializeDateInputs();
    this.setupCalendar();
  }

  private async loadData(): Promise<void> {
    // Get API Data
    await this.getHouseholdUsers();

    // Initialize date range to next Monday-Sunday (already done in setupListeners)
    // this.initializeDateInputs();

    // Generate initial dateList from default range
    this.dateList = [];
    const currentDate = new Date(this.startDate);
    while (currentDate <= this.endDate) {
      this.dateList.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Fetch plans for initial date range
    const startDateStr = this.startDate.toISOString().split("T")[0];
    const endDateStr = this.endDate.toISOString().split("T")[0];
    await this.planStore.getPlans(true, startDateStr, endDateStr);

    this.populatePlans();
  }


  private async getHouseholdUsers(): Promise<void> {
    try {
      const householdUsers = await getApi(apiUrl, "household", apiToken);
      if (householdUsers.some((user: IUser) => !user || !user.email)) {
        throw new Error("Invalid user data received from API - some users are undefined or missing email");
      }
      this.householdUsers = householdUsers;
      saveJsonToLocalStorage("householdUsers", householdUsers);
    } catch (e: any) {
      this.message.timedMessage(`⚠ Unexpected error getting household users ⚠`);
      console.log(e);
      getElementById("plan_loading").classList.add("hidden");
      getElementById("plan_content").classList.add("hidden")
      getElementById("plan_error").classList.remove("hidden");
    }
  }

  private populatePlans(): void {
    // Populate Day Of Week Headers
    const dateListTemplate = document.querySelector("[data-for-each=dateList]") as HTMLElement;
    const plan_plan_content = getElementById("plan_plan_content")
    plan_plan_content.innerHTML = "";
    this.dateList.forEach((date) => {
      const contentElement = dateListTemplate.cloneNode(true) as HTMLElement;
      this.populateDate(date, contentElement);
      plan_plan_content.appendChild(contentElement);
    });
    getElementById("plan_plan").classList.remove("hidden")
  }

  private populateDate(date: Date, dateElement: HTMLElement,): void {
    const dayNameElement = getChildElementSelector(dateElement, "dayName");
    // dayIndex here is for showing the correct day of the week
    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;
    dayNameElement.textContent = `${StandardWeekdays[dayIndex]} ${DateAppends[date.getDate()]} ${Months[date.getMonth()]}`;
    const mealTemplate = dateElement.querySelector("[data-for-each=meal]") as HTMLElement;
    mealTemplate.classList.remove("hidden")
    const userNameTemplate = dateElement.querySelector("[data-for-each=userName]") as HTMLElement;
    userNameTemplate.classList.remove("hidden");
    const userNameTemplateParent = userNameTemplate.parentElement as HTMLElement;
    this.householdUsers.forEach((user) => {
      const userName = userNameTemplate.cloneNode(true) as HTMLElement;
      userName.textContent = user.first_name;
      userNameTemplateParent.appendChild(userName);
    });
    userNameTemplate.classList.add("hidden");
    userNameTemplate.remove();
    Meals.forEach((meal) => {
      const mealElement = mealTemplate.cloneNode(true) as HTMLElement;
      dateElement.appendChild(mealElement);
      getChildElementSelector(mealElement, "mealName").textContent = meal;
      const mealUserTemplate = getChildElementSelector(mealElement, "mealPlanItem");
      getChildElementSelector(mealElement, "userNameContainer").classList.add("grid", `grid-cols-${this.householdUsers.length}`);
      this.householdUsers.forEach((user) => {
        const templateItem = new MealPlanItem(user, date.toISOString().split('T')[0], meal);
        mealUserTemplate.appendChild(templateItem);
      })
      mealUserTemplate.classList.add("grid", `grid-cols-${this.householdUsers.length}`);
    })
    mealTemplate.classList.add("hidden");
  }

  private initializeDateInputs(): void {
    // Set default start date to next Monday
    const today = new Date();
    const nextMonday = new Date();
    nextMonday.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7 || 7));

    // Set default end date to next Sunday (6 days after Monday)
    const nextSunday = new Date(nextMonday);
    nextSunday.setDate(nextMonday.getDate() + 6);

    this.startDate = nextMonday;
    this.endDate = nextSunday;
    this.currentMonth = new Date(nextMonday);

    // Update display
    this.updateDateRangeDisplay();
  }

  private async loadDateRange(): Promise<void> {
    if (this.startDate > this.endDate) {
      this.message.timedMessage(`⚠ Start date must be before end date ⚠`);
      return;
    }

    setSmallLoading(getElementById("plan_plan"));

    // Generate dateList from range
    this.dateList = [];
    const currentDate = new Date(this.startDate);
    while (currentDate <= this.endDate) {
      this.dateList.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Fetch plans for date range
    const startDateStr = this.startDate.toISOString().split("T")[0];
    const endDateStr = this.endDate.toISOString().split("T")[0];
    await this.planStore.getPlans(true, startDateStr, endDateStr);

    this.populatePlans();
    setContent("plan");
  }

  private setupCalendar(): void {
    const toggleButton = getElementById("toggleCalendarButton");
    const calendarContainer = getElementById("calendarContainer");
    const calendarDaysContainer = getElementById("calendarDays");
    const prevMonthBtn = getElementById("prevMonth");
    const nextMonthBtn = getElementById("nextMonth");
    const applyBtn = getElementById("applyDateRange");
    const clearBtn = getElementById("clearCalendar");

    // Toggle calendar visibility
    toggleButton.addEventListener("click", (e) => {
      e.stopPropagation();
      calendarContainer.classList.toggle("hidden");
      if (!calendarContainer.classList.contains("hidden")) {
        this.renderCalendar();
      }
    });

    // Use event delegation for date clicks
    calendarDaysContainer.addEventListener("click", (e) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      const button = target.closest("button[data-date]");
      if (button) {
        const dateStr = button.getAttribute("data-date");
        if (dateStr) {
          const date = new Date(dateStr);
          this.handleDateClick(date);
        }
      }
    });

    // Use event delegation for date hover (mouseover bubbles, mouseenter doesn't)
    let lastHoveredDate: string | null = null;
    calendarDaysContainer.addEventListener("mouseover", (e) => {
      if (!this.isSelectingRange) return;
      const target = e.target as HTMLElement;
      const button = target.closest("button[data-date]");
      if (button) {
        const dateStr = button.getAttribute("data-date");
        if (dateStr && dateStr !== lastHoveredDate) {
          lastHoveredDate = dateStr;
          const date = new Date(dateStr);
          this.hoverDate = date;
          this.renderCalendar();
        }
      }
    });

    // Close calendar when clicking outside
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (!calendarContainer.contains(target) && !toggleButton.contains(target)) {
        calendarContainer.classList.add("hidden");
      }
    });

    // Month navigation
    prevMonthBtn.addEventListener("click", () => {
      this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
      this.renderCalendar();
    });

    nextMonthBtn.addEventListener("click", () => {
      this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
      this.renderCalendar();
    });

    // Apply date range
    applyBtn.addEventListener("click", async () => {
      calendarContainer.classList.add("hidden");
      await this.loadDateRange();
    });

    // Clear selection
    clearBtn.addEventListener("click", () => {
      this.isSelectingRange = false;
      this.hoverDate = null;
      this.renderCalendar();
    });
  }

  private renderCalendar(): void {
    const monthYear = getElementById("monthYear");
    const calendarDays = getElementById("calendarDays");

    // Update month/year display
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];
    monthYear.textContent = `${monthNames[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;

    // Clear existing days
    calendarDays.innerHTML = "";

    // Get first day of month and number of days
    const firstDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const lastDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Adjust for Monday-first week (0=Monday, 6=Sunday)
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      const emptyDay = document.createElement("div");
      emptyDay.className = "bg-gray-800 py-1.5";
      calendarDays.appendChild(emptyDay);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), day);
      const dayElement = document.createElement("button");
      dayElement.type = "button";

      // Time element for better semantic HTML
      const timeElement = document.createElement("time");
      timeElement.dateTime = date.toISOString().split("T")[0];
      timeElement.textContent = day.toString();
      dayElement.appendChild(timeElement);

      // Base classes
      let classes = "bg-gray-800 py-1.5 hover:bg-gray-700 focus:z-10";

      // Apply styling based on selection state
      const dateStr = this.formatDateKey(date);
      const startStr = this.formatDateKey(this.startDate);
      const endStr = this.formatDateKey(this.endDate);
      const isStart = dateStr === startStr;
      const isEnd = dateStr === endStr;
      const isInRange = date > this.startDate && date < this.endDate;

      // Check hover range preview
      let isInHoverRange = false;
      if (this.hoverDate && this.isSelectingRange) {
        const rangeStart = this.startDate < this.hoverDate ? this.startDate : this.hoverDate;
        const rangeEnd = this.startDate < this.hoverDate ? this.hoverDate : this.startDate;
        isInHoverRange = date > rangeStart && date < rangeEnd;
      }

      if (isStart || isEnd) {
        // Selected start or end date
        classes = "bg-indigo-600 font-semibold text-white hover:bg-indigo-500 focus:z-10";
      } else if (isInRange) {
        // Dates in selected range
        classes = "bg-indigo-600 bg-opacity-30 text-indigo-100 hover:bg-opacity-40 focus:z-10";
      } else if (isInHoverRange) {
        // Dates in hover preview range
        classes = "bg-indigo-600 bg-opacity-10 text-indigo-200 hover:bg-opacity-20 focus:z-10";
      }

      dayElement.className = classes;

      // Add data-date attribute for event delegation
      dayElement.setAttribute("data-date", date.toISOString());

      calendarDays.appendChild(dayElement);
    }

    // Fill remaining cells in the last week
    const totalCells = calendarDays.children.length;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 0; i < remainingCells; i++) {
      const emptyDay = document.createElement("div");
      emptyDay.className = "bg-gray-800 py-1.5";
      calendarDays.appendChild(emptyDay);
    }
  }

  private handleDateClick(date: Date): void {
    if (!this.isSelectingRange) {
      // First click - set start date
      this.startDate = new Date(date);
      this.endDate = new Date(date);
      this.isSelectingRange = true;
      this.updateDateRangeDisplay();
    } else {
      // Second click - set end date
      if (date < this.startDate) {
        // If clicked date is before start, swap them
        this.endDate = new Date(this.startDate);
        this.startDate = new Date(date);
      } else {
        this.endDate = new Date(date);
      }
      this.isSelectingRange = false;
      this.hoverDate = null;
      this.updateDateRangeDisplay();
    }
    this.renderCalendar();
  }

  private updateDateRangeDisplay(): void {
    const display = getElementById("dateRangeDisplay");
    const startStr = this.formatDate(this.startDate);
    const endStr = this.formatDate(this.endDate);

    if (this.startDate.getTime() === this.endDate.getTime()) {
      display.textContent = startStr;
    } else {
      display.textContent = `${startStr} - ${endStr}`;
    }
  }

  private formatDate(date: Date): string {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  }

  private formatDateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }
}
